import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FiArrowRight,
  FiCheck,
  FiHome,
  FiSearch,
  FiSliders,
  FiX,
} from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { useCart } from "./CartContext";
import ProductCard from "./ProductCard";
import "../css/shop.scss";

const sortOptions = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "price-asc", label: "Price: low to high" },
  { value: "price-desc", label: "Price: high to low" },
  { value: "name", label: "Name: A–Z" },
];

const categories = [...new Set(productData.map((product) => product.category))];

const tagOptions = Object.entries(
  productData.reduce((counts, product) => {
    product.tags.forEach((tag) => {
      counts[tag] = (counts[tag] || 0) + 1;
    });
    return counts;
  }, {})
)
  .sort(([tagA, countA], [tagB, countB]) => countB - countA || tagA.localeCompare(tagB))
  .map(([tag, count]) => ({ tag, count }));

const priceCeiling = Math.ceil(
  Math.max(...productData.map((product) => Number(product.price)))
);

const filterKeys = [
  "q",
  "category",
  "min",
  "max",
  "availability",
  "wishlist",
  "tags",
];

const formatTag = (tag) =>
  tag
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getListParam = (params, key) =>
  params
    .getAll(key)
    .flatMap((value) => value.split(","))
    .map((value) => value.trim())
    .filter(Boolean);

const getNumberParam = (params, key) => {
  const rawValue = params.get(key);
  if (rawValue === null || rawValue === "") return null;
  const value = Number(rawValue);
  return Number.isFinite(value) && value >= 0 ? value : null;
};

const productSearchText = (product) =>
  [
    product.name,
    product.category,
    product.description,
    product.sku,
    product.tags.join(" "),
    Object.values(product.attributes).join(" "),
  ]
    .join(" ")
    .toLowerCase();

function FilterPanel({
  activeFilterCount,
  availability,
  idPrefix,
  maxPrice,
  minPrice,
  onClear,
  onPriceChange,
  onSetAvailability,
  onSetWishlistOnly,
  onToggleCategory,
  onToggleTag,
  selectedCategories,
  selectedTags,
  wishlistCount,
  wishlistOnly,
}) {
  return (
    <div className="shop-filters">
      <div className="shop-filters__heading">
        <h2>Filters</h2>
        <button disabled={!activeFilterCount} onClick={onClear} type="button">
          Clear all
        </button>
      </div>

      <fieldset className="shop-filters__group">
        <legend>Category</legend>
        <div className="shop-filters__options">
          {categories.map((category, index) => {
            const inputId = `${idPrefix}-category-${index}`;
            const count = productData.filter((product) => product.category === category).length;
            return (
              <label htmlFor={inputId} key={category}>
                <input
                  checked={selectedCategories.includes(category)}
                  id={inputId}
                  onChange={() => onToggleCategory(category)}
                  type="checkbox"
                />
                <span className="shop-filters__checkbox" aria-hidden="true">
                  <FiCheck />
                </span>
                <span>{category}</span>
                <small>{count}</small>
              </label>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="shop-filters__group">
        <legend>Availability</legend>
        <div className="shop-filters__options">
          {[
            { value: "", label: "All products", count: productData.length },
            {
              value: "in-stock",
              label: "In stock",
              count: productData.filter((product) => product.stock > 0).length,
            },
            {
              value: "out-of-stock",
              label: "Out of stock",
              count: productData.filter((product) => product.stock <= 0).length,
            },
          ].map((option, index) => {
            const inputId = `${idPrefix}-availability-${index}`;
            return (
              <label htmlFor={inputId} key={option.label}>
                <input
                  checked={availability === option.value}
                  id={inputId}
                  name={`${idPrefix}-availability`}
                  onChange={() => onSetAvailability(option.value)}
                  type="radio"
                />
                <span className="shop-filters__radio" aria-hidden="true" />
                <span>{option.label}</span>
                <small>{option.count}</small>
              </label>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="shop-filters__group">
        <legend>Price</legend>
        <p className="shop-filters__price-note">
          The highest price is {siteConfig.currencySymbol}{priceCeiling}.
        </p>
        <div className="shop-filters__price">
          <label htmlFor={`${idPrefix}-min-price`}>
            <span>From</span>
            <span className="shop-filters__money-input">
              <span aria-hidden="true">{siteConfig.currencySymbol}</span>
              <input
                id={`${idPrefix}-min-price`}
                inputMode="numeric"
                max={priceCeiling}
                min="0"
                onChange={(event) => onPriceChange("min", event.target.value)}
                placeholder="0"
                type="number"
                value={minPrice}
              />
            </span>
          </label>
          <span aria-hidden="true">—</span>
          <label htmlFor={`${idPrefix}-max-price`}>
            <span>To</span>
            <span className="shop-filters__money-input">
              <span aria-hidden="true">{siteConfig.currencySymbol}</span>
              <input
                id={`${idPrefix}-max-price`}
                inputMode="numeric"
                max={priceCeiling}
                min="0"
                onChange={(event) => onPriceChange("max", event.target.value)}
                placeholder={String(priceCeiling)}
                type="number"
                value={maxPrice}
              />
            </span>
          </label>
        </div>
      </fieldset>

      <fieldset className="shop-filters__group">
        <legend>Saved items</legend>
        <div className="shop-filters__options">
          <label htmlFor={`${idPrefix}-wishlist`}>
            <input
              checked={wishlistOnly}
              id={`${idPrefix}-wishlist`}
              onChange={(event) => onSetWishlistOnly(event.target.checked)}
              type="checkbox"
            />
            <span className="shop-filters__checkbox" aria-hidden="true">
              <FiCheck />
            </span>
            <span>Wishlist only</span>
            <small>{wishlistCount}</small>
          </label>
        </div>
      </fieldset>

      <fieldset className="shop-filters__group shop-filters__group--tags">
        <legend>Tags</legend>
        <div className="shop-filters__options">
          {tagOptions.map(({ tag, count }, index) => {
            const inputId = `${idPrefix}-tag-${index}`;
            return (
              <label htmlFor={inputId} key={tag}>
                <input
                  checked={selectedTags.includes(tag)}
                  id={inputId}
                  onChange={() => onToggleTag(tag)}
                  type="checkbox"
                />
                <span className="shop-filters__checkbox" aria-hidden="true">
                  <FiCheck />
                </span>
                <span>{formatTag(tag)}</span>
                <small>{count}</small>
              </label>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { wishlistItems } = useCart();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState(searchParams.get("q") || "");
  const closeDrawerRef = useRef(null);
  const drawerRef = useRef(null);
  const filterButtonRef = useRef(null);

  const query = (searchParams.get("q") || "").trim();
  const selectedCategories = getListParam(searchParams, "category");
  const selectedTags = getListParam(searchParams, "tags");
  const minPriceParam = searchParams.get("min") || "";
  const maxPriceParam = searchParams.get("max") || "";
  const minPrice = getNumberParam(searchParams, "min");
  const maxPrice = getNumberParam(searchParams, "max");
  const availability = ["in-stock", "out-of-stock"].includes(
    searchParams.get("availability")
  )
    ? searchParams.get("availability")
    : "";
  const wishlistOnly = ["1", "true", "yes"].includes(
    (searchParams.get("wishlist") || "").toLowerCase()
  );
  const requestedSort = searchParams.get("sort") || "featured";
  const sort = sortOptions.some((option) => option.value === requestedSort)
    ? requestedSort
    : "featured";

  const updateParams = (updates, options = { replace: true }) => {
    const nextParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === "" || value === null || value === undefined || value === false) {
        nextParams.delete(key);
      } else {
        nextParams.set(key, String(value));
      }
    });

    setSearchParams(nextParams, options);
  };

  const toggleListParam = (key, value) => {
    const selected = new Set(getListParam(searchParams, key));
    if (selected.has(value)) selected.delete(value);
    else selected.add(value);
    updateParams({ [key]: [...selected].join(",") });
  };

  const clearAllFilters = () => {
    const nextParams = new URLSearchParams(searchParams);
    filterKeys.forEach((key) => nextParams.delete(key));
    setSearchDraft("");
    setSearchParams(nextParams, { replace: true });
  };

  const removeFilter = (key, value) => {
    if (key === "category" || key === "tags") {
      const remaining = getListParam(searchParams, key).filter((item) => item !== value);
      updateParams({ [key]: remaining.join(",") });
      return;
    }

    if (key === "q") setSearchDraft("");
    updateParams({ [key]: "" });
  };

  const activeFilters = [
    ...(query ? [{ key: "q", value: query, label: `Search: “${query}”` }] : []),
    ...selectedCategories.map((category) => ({
      key: "category",
      value: category,
      label: category,
    })),
    ...selectedTags.map((tag) => ({ key: "tags", value: tag, label: formatTag(tag) })),
    ...(minPrice !== null
      ? [{ key: "min", value: minPrice, label: `From ${siteConfig.currencySymbol}${minPrice}` }]
      : []),
    ...(maxPrice !== null
      ? [{ key: "max", value: maxPrice, label: `To ${siteConfig.currencySymbol}${maxPrice}` }]
      : []),
    ...(availability
      ? [
          {
            key: "availability",
            value: availability,
            label: availability === "in-stock" ? "In stock" : "Out of stock",
          },
        ]
      : []),
    ...(wishlistOnly
      ? [{ key: "wishlist", value: "true", label: "Wishlist only" }]
      : []),
  ];

  const visibleProducts = useMemo(() => {
    const normalizedQuery = query.toLowerCase();
    const wishlistSet = new Set(wishlistItems);

    const filtered = productData.filter((product) => {
      if (normalizedQuery && !productSearchText(product).includes(normalizedQuery)) return false;
      if (
        selectedCategories.length &&
        !selectedCategories.some(
          (category) => category.toLowerCase() === product.category.toLowerCase()
        )
      ) {
        return false;
      }
      if (selectedTags.length && !selectedTags.some((tag) => product.tags.includes(tag))) {
        return false;
      }
      if (minPrice !== null && Number(product.price) < minPrice) return false;
      if (maxPrice !== null && Number(product.price) > maxPrice) return false;
      if (availability === "in-stock" && product.stock <= 0) return false;
      if (availability === "out-of-stock" && product.stock > 0) return false;
      if (wishlistOnly && !wishlistSet.has(product.id)) return false;
      return true;
    });

    return [...filtered].sort((productA, productB) => {
      switch (sort) {
        case "newest":
          return Number(productB.newArrival) - Number(productA.newArrival) || productB.id - productA.id;
        case "price-asc":
          return Number(productA.price) - Number(productB.price) || productA.id - productB.id;
        case "price-desc":
          return Number(productB.price) - Number(productA.price) || productA.id - productB.id;
        case "name":
          return productA.name.localeCompare(productB.name);
        default:
          return Number(productB.featured) - Number(productA.featured) || productA.id - productB.id;
      }
    });
  }, [
    availability,
    maxPrice,
    minPrice,
    query,
    selectedCategories,
    selectedTags,
    sort,
    wishlistItems,
    wishlistOnly,
  ]);

  useEffect(() => {
    setSearchDraft(query);
  }, [query]);

  useEffect(() => {
    if (!isFilterDrawerOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeDrawerRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsFilterDrawerOpen(false);
        window.requestAnimationFrame(() => filterButtonRef.current?.focus());
      }

      if (event.key === "Tab") {
        const focusableElements = drawerRef.current?.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href]'
        );
        if (!focusableElements?.length) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFilterDrawerOpen]);

  const closeFilterDrawer = () => {
    setIsFilterDrawerOpen(false);
    window.requestAnimationFrame(() => filterButtonRef.current?.focus());
  };

  const filterPanelProps = {
    activeFilterCount: activeFilters.length,
    availability,
    maxPrice: maxPriceParam,
    minPrice: minPriceParam,
    onClear: clearAllFilters,
    onPriceChange: (key, value) => updateParams({ [key]: value }),
    onSetAvailability: (value) => updateParams({ availability: value }),
    onSetWishlistOnly: (checked) => updateParams({ wishlist: checked ? "true" : "" }),
    onToggleCategory: (category) => toggleListParam("category", category),
    onToggleTag: (tag) => toggleListParam("tags", tag),
    selectedCategories,
    selectedTags,
    wishlistCount: wishlistItems.length,
    wishlistOnly,
  };

  return (
    <main className="shop-page">
      <section className="shop-page__hero">
        <div className="shop-page__hero-copy">
          <nav aria-label="Breadcrumb" className="shop-page__breadcrumb">
            <Link to="/">
              <FiHome aria-hidden="true" /> Home
            </Link>
            <span aria-hidden="true">/</span>
            <span>Shop</span>
          </nav>
          <p className="shop-page__eyebrow">The full pantry · {productData.length} products</p>
          <h1>Something for every food lover.</h1>
          <p>
            Explore expressive cheeses, bold charcuterie, pantry discoveries,
            thoughtful gifts, and curated surprises selected for the table.
          </p>

          <form
            className="shop-page__search"
            onSubmit={(event) => {
              event.preventDefault();
              updateParams({ q: searchDraft.trim() }, { replace: false });
            }}
            role="search"
          >
            <label className="shop-page__visually-hidden" htmlFor="catalog-search">
              Search the product catalog
            </label>
            <FiSearch aria-hidden="true" />
            <input
              id="catalog-search"
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder="Search cheese, gifts, pantry…"
              type="search"
              value={searchDraft}
            />
            {searchDraft && (
              <button
                aria-label="Clear search"
                className="shop-page__search-clear"
                onClick={() => {
                  setSearchDraft("");
                  updateParams({ q: "" });
                }}
                type="button"
              >
                <FiX aria-hidden="true" />
              </button>
            )}
            <button className="shop-page__search-submit" type="submit">
              Search
            </button>
          </form>
        </div>
        <div className="shop-page__hero-image">
          <img
            alt="A generous gourmet market spread"
            src="/img/editorial/shop-market.png"
          />
        </div>
      </section>

      <section className="shop-page__catalog" aria-labelledby="catalog-title">
        <aside className="shop-page__desktop-filters" aria-label="Product filters">
          <FilterPanel idPrefix="desktop" {...filterPanelProps} />
        </aside>

        <div className="shop-page__results">
          <div className="shop-page__toolbar">
            <div>
              <h2 id="catalog-title">
                {visibleProducts.length === productData.length
                  ? `${productData.length} products`
                  : `${visibleProducts.length} of ${productData.length} products`}
              </h2>
              <p aria-live="polite">
                {activeFilters.length
                  ? `${activeFilters.length} active ${
                      activeFilters.length === 1 ? "filter" : "filters"
                    }`
                  : "Showing the complete collection"}
              </p>
            </div>

            <div className="shop-page__toolbar-actions">
              <button
                aria-controls="mobile-filter-drawer"
                aria-expanded={isFilterDrawerOpen}
                className="shop-page__filter-trigger"
                onClick={() => setIsFilterDrawerOpen(true)}
                ref={filterButtonRef}
                type="button"
              >
                <FiSliders aria-hidden="true" /> Filters
                {activeFilters.length > 0 && <span>{activeFilters.length}</span>}
              </button>

              <label className="shop-page__sort" htmlFor="catalog-sort">
                <span>Sort by</span>
                <select
                  id="catalog-sort"
                  onChange={(event) => updateParams({ sort: event.target.value })}
                  value={sort}
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {activeFilters.length > 0 && (
            <div className="shop-page__active-filters" aria-label="Active filters">
              {activeFilters.map((filter) => (
                <button
                  key={`${filter.key}-${filter.value}`}
                  onClick={() => removeFilter(filter.key, filter.value)}
                  type="button"
                >
                  {filter.label}
                  <FiX aria-hidden="true" />
                </button>
              ))}
              <button className="shop-page__clear-link" onClick={clearAllFilters} type="button">
                Clear all
              </button>
            </div>
          )}

          {visibleProducts.length > 0 ? (
            <div className="shop-page__grid">
              {visibleProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="shop-page__empty">
              <span aria-hidden="true">0</span>
              <p className="shop-page__eyebrow">No matches</p>
              <h2>Nothing on this shelf—yet.</h2>
              <p>
                Try a broader search, adjust the price range, or clear your filters
                to see the complete pantry.
              </p>
              <button onClick={clearAllFilters} type="button">
                Clear all filters <FiArrowRight aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </section>

      {isFilterDrawerOpen && (
        <div className="shop-page__drawer-layer">
          <button
            aria-label="Close product filters"
            className="shop-page__drawer-backdrop"
            onClick={closeFilterDrawer}
            type="button"
          />
          <aside
            aria-label="Product filters"
            aria-modal="true"
            className="shop-page__drawer"
            id="mobile-filter-drawer"
            ref={drawerRef}
            role="dialog"
          >
            <div className="shop-page__drawer-heading">
              <div>
                <p>Refine the pantry</p>
                <h2>Filters</h2>
              </div>
              <button
                aria-label="Close filters"
                onClick={closeFilterDrawer}
                ref={closeDrawerRef}
                type="button"
              >
                <FiX aria-hidden="true" />
              </button>
            </div>
            <div className="shop-page__drawer-body">
              <FilterPanel idPrefix="mobile" {...filterPanelProps} />
            </div>
            <div className="shop-page__drawer-footer">
              <button onClick={closeFilterDrawer} type="button">
                Show {visibleProducts.length} {visibleProducts.length === 1 ? "product" : "products"}
              </button>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
}

export default Shop;
